import {
  useEffect,
  useState,
  forwardRef,
  ComponentProps,
  ChangeEventHandler,
  ChangeEvent,
  useId,
  InputHTMLAttributes,
} from 'react'
import classNames from 'classnames'
import { Switch } from '@headlessui/react'
import { Spinner } from 'ui/components/Spinner'
import { CheckIcon } from '@heroicons/react/outline'
import { createEvent } from 'utilities/createEvent'
import { Text } from '../Text'
import { Label } from './Label'
import { Tooltip } from 'react-tooltip'
import type { OverrideProperties } from 'type-fest'

export interface ToggleProps
  extends Omit<ComponentProps<typeof Switch.Group<'div'>>, 'as' | 'onChange'> {
  /** The label for the toggle */
  label?: string
  name?: string
  /** An optional description for the toggle */
  description?: string | string[]
  /** The value for the toggle */
  value?: boolean
  /** Fired when the input is changed */
  onChange?: (checked: boolean) => void
  /** Whether the checkbox is in a loading state */
  loading?: boolean
  /** Whether the checkbox is in a success state */
  success?: boolean
  /** Whether the checkbox is in a disabled state */
  disabled?: boolean
  /** The reason the checkbox is disabled */
  disabledReason?: string
  type?: InputHTMLAttributes<Element>['type']
}

export const Toggle = forwardRef<unknown, ToggleProps>(
  (
    {
      label,
      description = [],
      id,
      value,
      onChange,
      loading,
      success,
      disabled = false,
      disabledReason,
      type: _type,
      ...props
    },
    _ref
  ) => {
    const fallbackId = useId()
    const [displaySuccess, setDisplaySuccess] = useState(false)

    useEffect(() => {
      if (success) {
        setDisplaySuccess(true)

        const timeout = setTimeout(() => {
          setDisplaySuccess(false)
        }, 3000)

        return () => clearTimeout(timeout)
      }
    }, [success])

    const toggleId = id ?? fallbackId

    return (
      <Switch.Group {...props} as="div">
        <div className="flex items-center">
          <div
            className="inline-flex"
            data-tooltip-content={disabled ? disabledReason : undefined}
            data-tooltip-id="disabled-toggle-tooltip"
            data-tooltip-place="top"
          >
            <Switch
              checked={value}
              onChange={onChange}
              disabled={disabled}
              id={toggleId}
              className={classNames(
                value ? 'bg-violet' : 'bg-background-toggle',
                'disabled:opacity-50 disabled:cursor-not-allowed relative inline-flex flex-shrink-0 h-5 w-9 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-violet'
              )}
            >
              <span
                aria-hidden
                className={classNames(
                  value ? 'translate-x-4' : 'translate-x-0',
                  'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200'
                )}
              />
            </Switch>

            {disabled && <Tooltip id="disabled-toggle-tooltip" />}
          </div>

          {label && (
            <Switch.Label
              as={Label}
              className={classNames({ 'opacity-50': disabled }, 'ml-3')}
              htmlFor={toggleId}
            >
              {label}
            </Switch.Label>
          )}

          {(loading || displaySuccess) && (
            <div className="inline-flex w-8">
              {loading && (
                <div className="ml-3">
                  <Spinner className="my-0" variant="info" />
                </div>
              )}

              {!loading && displaySuccess && (
                <span aria-label="Success">
                  <span className="sr-only">The toggle was successfully saved</span>
                  <CheckIcon
                    aria-hidden="true"
                    className="h-5 w-5 inline-block ml-3 text-green-700"
                  />
                </span>
              )}
            </div>
          )}
        </div>

        {description.length > 0 && (
          <Switch.Description as="div" className="text-sm text-gray-500 mt-2 space-y-2">
            {typeof description === 'string' && description}
            {Array.isArray(description) &&
              description.map((line, index) => (
                <Text type="caption" key={index}>
                  {line}
                </Text>
              ))}
          </Switch.Description>
        )}
      </Switch.Group>
    )
  }
)

type FormikToggleProps = OverrideProperties<ToggleProps, { onChange: ChangeEventHandler }>

export const FormikToggle = forwardRef<unknown, FormikToggleProps>(
  ({ onChange, ...props }, ref) => {
    const handleOnChange: ToggleProps['onChange'] = (value) => {
      onChange?.(
        createEvent<ChangeEvent<HTMLInputElement>>({
          // @ts-expect-error Overriding value as boolean.
          value: value,
          name: props.name,
        })
      )
    }

    return <Toggle onChange={handleOnChange} ref={ref} {...props} />
  }
)
